/**
 * The inputs a node's parameters are edited with. Every text-shaped one is
 * *mappable*: beside it sits a `{x}` button that opens the data picker, and
 * the field it picks is inserted at the caret as an expression.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { DataPicker } from './DataPicker'
import type { Source } from './useSources'
import type { NodeSpec } from '../types'

export interface MapContext {
  sources: Source[]
  specs: Record<string, NodeSpec>
}

export function asText(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

/** One line: a value, or an expression, with the picker beside it. */
export function MappableInput({
  value,
  placeholder,
  onChange,
  map,
  multiline,
  mono,
}: {
  value: unknown
  placeholder?: string
  onChange: (v: string) => void
  /** Null: a plain input, nothing earlier to map from. */
  map: MapContext | null
  multiline?: boolean
  mono?: boolean
}) {
  const text = asText(value)
  const isExpr = text.includes('{{')
  const [open, setOpen] = useState(false)
  const caret = useRef<number | null>(null)
  const el = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  const remember = () => {
    caret.current = el.current?.selectionStart ?? null
  }
  const insert = (expr: string) => {
    const at = caret.current ?? text.length
    const before = text.slice(0, at)
    const after = text.slice(at)
    const glue = before && !/\s$/.test(before) && !isExpr ? ' ' : ''
    onChange(before + glue + expr + after)
    setOpen(false)
    requestAnimationFrame(() => el.current?.focus())
  }

  const shared = {
    value: text,
    placeholder: placeholder || (map ? 'Type a value, or map one with {x}' : ''),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value),
    onSelect: remember,
    onKeyUp: remember,
    onClick: remember,
    onBlur: remember,
    spellCheck: false,
  }

  return (
    <div className="wf-map" data-expr={isExpr || undefined} data-open={open || undefined} data-plain={map ? undefined : true}>
      {multiline ? (
        <textarea ref={el as React.RefObject<HTMLTextAreaElement>} className={`textarea${mono || isExpr ? ' textarea-mono' : ''}`} rows={4} {...shared} />
      ) : (
        <input ref={el as React.RefObject<HTMLInputElement>} className={`input${mono || isExpr ? ' input-mono' : ''}`} {...shared} />
      )}
      {map && (
        <button
          type="button"
          className="wf-map-btn"
          data-on={open || undefined}
          title="Map a value from an earlier step"
          aria-label="Map a value from an earlier step"
          onClick={() => setOpen((v) => !v)}
        >
          {'{x}'}
        </button>
      )}
      {open && map && <DataPicker sources={map.sources} specs={map.specs} onInsert={insert} onClose={() => setOpen(false)} />}
    </div>
  )
}

/** JSON, kept as text while it is being typed; parsed on blur when it can be. */
export function JsonInput({ value, placeholder, onChange, map, rows = 5 }: { value: unknown; placeholder?: string; onChange: (v: unknown) => void; map: MapContext | null; rows?: number }) {
  const initial = useMemo(() => (typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value, null, 2)), [value])
  const [text, setText] = useState(initial)
  const [bad, setBad] = useState(false)
  const [open, setOpen] = useState(false)
  const el = useRef<HTMLTextAreaElement>(null)
  const caret = useRef<number | null>(null)
  useEffect(() => setText(initial), [initial])

  const commit = (t: string) => {
    const trimmed = t.trim()
    if (!trimmed) return onChange(null)
    if (trimmed.includes('{{')) {
      setBad(false)
      return onChange(t)
    }
    try {
      onChange(JSON.parse(trimmed))
      setBad(false)
    } catch {
      setBad(true)
      onChange(t)
    }
  }
  const insert = (expr: string) => {
    const at = caret.current ?? text.length
    const next = text.slice(0, at) + expr + text.slice(at)
    setText(next)
    commit(next)
    setOpen(false)
  }
  return (
    <div className="wf-map" data-open={open || undefined} data-plain={map ? undefined : true}>
      <textarea
        ref={el}
        className="textarea textarea-mono"
        rows={rows}
        value={text}
        placeholder={placeholder || '{ "key": "{{ $json.value }}" }'}
        aria-invalid={bad || undefined}
        spellCheck={false}
        onChange={(e) => setText(e.target.value)}
        onSelect={() => (caret.current = el.current?.selectionStart ?? null)}
        onKeyUp={() => (caret.current = el.current?.selectionStart ?? null)}
        onBlur={() => {
          caret.current = el.current?.selectionStart ?? null
          commit(text)
        }}
      />
      {map && <button type="button" className="wf-map-btn" data-on={open || undefined} title="Map a value" aria-label="Map a value" onClick={() => setOpen((v) => !v)}>{'{x}'}</button>}
      {bad && <p className="field-error">Not valid JSON — it will be sent as text.</p>}
      {open && map && <DataPicker sources={map.sources} specs={map.specs} onInsert={insert} onClose={() => setOpen(false)} />}
    </div>
  )
}

export function CodeInput({ value, onChange, map }: { value: string; onChange: (v: string) => void; map: MapContext }) {
  const [open, setOpen] = useState(false)
  const el = useRef<HTMLTextAreaElement>(null)
  const caret = useRef<number | null>(null)
  const insert = (expr: string) => {
    // Inside code the braces are not needed: `$json.x` is already an expression.
    const bare = expr.replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '')
    const at = caret.current ?? value.length
    onChange(value.slice(0, at) + bare + value.slice(at))
    setOpen(false)
  }
  return (
    <div className="wf-map" data-open={open || undefined}>
      <textarea
        ref={el}
        className="textarea textarea-mono wf-code"
        rows={12}
        value={value}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        onSelect={() => (caret.current = el.current?.selectionStart ?? null)}
        onKeyUp={() => (caret.current = el.current?.selectionStart ?? null)}
        onBlur={() => (caret.current = el.current?.selectionStart ?? null)}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            e.preventDefault()
            const t = e.currentTarget
            const s = t.selectionStart
            const next = `${value.slice(0, s)}    ${value.slice(t.selectionEnd)}`
            onChange(next)
            requestAnimationFrame(() => t.setSelectionRange(s + 4, s + 4))
          }
        }}
      />
      <button type="button" className="wf-map-btn" data-on={open || undefined} title="Insert a value" aria-label="Insert a value" onClick={() => setOpen((v) => !v)}>{'{x}'}</button>
      {open && <DataPicker sources={map.sources} specs={map.specs} onInsert={insert} onClose={() => setOpen(false)} />}
    </div>
  )
}

/** Rows of key + value, the value mappable. Used for Set fields, headers, query, variables. */
export function RowsInput({
  rows,
  keyPlaceholder,
  valuePlaceholder,
  onChange,
  addLabel,
  map,
  valueKey = 'value',
  keyKey = 'key',
}: {
  rows: Record<string, unknown>[]
  keyPlaceholder: string
  valuePlaceholder: string
  onChange: (rows: Record<string, unknown>[]) => void
  addLabel: string
  map: MapContext | null
  valueKey?: string
  keyKey?: string
}) {
  const set = (i: number, k: string, v: unknown) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)))
  return (
    <div className="wf-rows">
      {rows.map((r, i) => (
        <div className="wf-row" key={i}>
          <input className="input input-mono" value={String(r[keyKey] ?? '')} placeholder={keyPlaceholder} onChange={(e) => set(i, keyKey, e.target.value)} aria-label="Name" />
          <MappableInput value={r[valueKey]} placeholder={valuePlaceholder} onChange={(v) => set(i, valueKey, v)} map={map} />
          <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label="Remove" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>×</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...rows, { [keyKey]: '', [valueKey]: '' }])}>+ {addLabel}</button>
    </div>
  )
}

export function MapInput({ value, onChange, map, keyPlaceholder = 'name', valuePlaceholder = 'value or {{ expression }}' }: { value: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void; map: MapContext | null; keyPlaceholder?: string; valuePlaceholder?: string }) {
  // A row without a name cannot live in the object yet, so rows are kept here
  // and only the named ones are written out. The local list is re-derived
  // when the object changes underneath (another node selected, a run loaded).
  const derive = (v: Record<string, unknown>) => Object.entries(v ?? {}).map(([key, val]) => ({ key, value: val }))
  const [rows, setRows] = useState<{ key: string; value: unknown }[]>(() => derive(value))
  const stamp = JSON.stringify(value ?? {})
  const lastStamp = useRef(stamp)
  useEffect(() => {
    if (stamp === lastStamp.current) return
    lastStamp.current = stamp
    const named = JSON.stringify(Object.fromEntries(rows.filter((r) => r.key).map((r) => [r.key, r.value])))
    if (named !== stamp) setRows(derive(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp])
  const commit = (next: { key: string; value: unknown }[]) => {
    setRows(next)
    const obj = Object.fromEntries(next.filter((r) => r.key).map((r) => [r.key, r.value]))
    lastStamp.current = JSON.stringify(obj)
    onChange(obj)
  }
  return (
    <RowsInput
      rows={rows}
      keyPlaceholder={keyPlaceholder}
      valuePlaceholder={valuePlaceholder}
      addLabel="Add"
      map={map}
      onChange={(next) => commit(next.map((r) => ({ key: String(r.key ?? ''), value: r.value })))}
    />
  )
}

export function Field({ label, required, help, children, mono }: { label: ReactNode; required?: boolean; help?: ReactNode; children: ReactNode; mono?: boolean }) {
  return (
    <div className="field wf-field">
      <span className={`field-label${mono ? ' mono' : ''}`}>
        {label}
        {required && <span className="field-req">*</span>}
      </span>
      {children}
      {help && <p className="field-help">{help}</p>}
    </div>
  )
}
